import { AuthFooter } from "components/design/authFooter";
import { ConditionalLink } from "components/conditionalLink";
import { Button } from "components/DesignSystem/Button/Button";
import Input from "components/DesignSystem/Input/Input";
import { Spinner } from "components/DesignSystem/Loader/loader";
import { FormikForm } from "components/FormikForm/FormikForm";
import { RightArrow } from "components/icons/RightArrow";
import { useToast } from "hooks/useToast";
import { ChangeEvent, ReactNode, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useGetCrmUserQuery } from "store/apis/onboarding";
import { useUpdateNameMutation } from "store/apis/settings";
import { BackendError } from "types/utils/error";
import * as Yup from "yup";
import { useOnboardingData } from "../../OnboardingDataContext";
import { buildPersonalDetailsSummary } from "../PersonalDetailsStep/PersonalDetailsStep";

type InvitedDetailsStepProps = {
  onComplete: (summary: ReactNode) => void;
};

type Details = { firstName: string; lastName: string; email: string };

const PAYLOAD_PARAM = "payload";

const validationSchema = Yup.object({
  firstName: Yup.string().trim().required("First name is required"),
  lastName: Yup.string().trim().required("Last name is required"),
});

export const InvitedDetailsStep = ({ onComplete }: InvitedDetailsStepProps) => {
  const { hash } = useLocation();
  const { patchData } = useOnboardingData();
  const { alertToast } = useToast();

  const [isTermsChecked, setIsTermsChecked] = useState(false);
  const [showTermsError, setShowTermsError] = useState(false);

  const crmAccessToken =
    new URLSearchParams((hash ?? "").replace(/^#/, "")).get(PAYLOAD_PARAM) ??
    "";

  const {
    data: invitedUser,
    isLoading,
    isError,
    error: inviteError,
  } = useGetCrmUserQuery({ crmAccessToken }, { skip: !crmAccessToken });

  const inviteErrorMessage =
    (inviteError as BackendError | undefined)?.data?.error?.message ??
    "This invite link is no longer valid. Please ask for a new invite.";

  useEffect(() => {
    if (!isError) return;

    alertToast({ message: inviteErrorMessage });
  }, [isError, inviteErrorMessage, alertToast]);

  const [updateName, { isLoading: isSaving }] = useUpdateNameMutation();

  const handleTermsChange = (event: ChangeEvent<HTMLInputElement>) => {
    setIsTermsChecked(event.target.checked);
    if (event.target.checked) setShowTermsError(false);
  };

  const saveDetails = async (values: Details) => {
    if (!invitedUser?.uuid) return;

    try {
      const profile = await updateName({
        payload: {
          first_name: values.firstName.trim(),
          last_name: values.lastName.trim(),
          profile_id: invitedUser.uuid,
        },
      }).unwrap();

      patchData({
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        email: values.email,
        profileId: profile.uuid || invitedUser.uuid,
      });
      onComplete(
        buildPersonalDetailsSummary({
          firstName: values.firstName.trim(),
          lastName: values.lastName.trim(),
          email: values.email,
        })
      );
    } catch (error) {
      alertToast({
        message:
          (error as BackendError)?.data?.error?.message ??
          "Could not save your details. Please try again.",
      });
    }
  };

  const handleSubmit = (values: Details) => {
    if (!isTermsChecked) {
      setShowTermsError(true);
      return;
    }
    void saveDetails(values);
  };

  if (isLoading) {
    return (
      <div className="t-flex t-justify-center t-py-4">
        <Spinner />
      </div>
    );
  }

  if (!crmAccessToken || isError || !invitedUser?.uuid) {
    return (
      <div className="t-flex t-flex-col t-items-start t-gap-3">
        <p className="t-m-0 t-text-body-sm t-text-red">{inviteErrorMessage}</p>
        <ConditionalLink to="/signin">Log in instead</ConditionalLink>
      </div>
    );
  }

  const initialValues: Details = {
    firstName: invitedUser?.first_name || "",
    lastName: invitedUser?.last_name || "",
    email: invitedUser?.email || "",
  };

  return (
    <FormikForm<Details>
      initialValues={initialValues}
      enableReinitialize
      validationSchema={validationSchema}
      validateOnChange={false}
      onSubmit={handleSubmit}
    >
      <div className="t-flex t-flex-col t-gap-4">
        <div className="t-grid t-grid-cols-2 sm:t-grid-cols-1 t-gap-3">
          <Input
            withForm
            required
            name="firstName"
            label="First name"
            placeholder="John"
          />
          <Input
            withForm
            required
            name="lastName"
            label="Last name"
            placeholder="Doe"
          />
        </div>
        <Input withForm name="email" label="Work email" disabled />
        <AuthFooter
          isChecked={isTermsChecked}
          onChange={handleTermsChange}
          showError={showTermsError}
        />
        <div>
          <Button
            type="submit"
            customType="primary"
            isLoading={isSaving}
            rightIcon={<RightArrow />}
          >
            Continue
          </Button>
        </div>
      </div>
    </FormikForm>
  );
};
